import { ROOM_MATCH_LOCK_STALE_MS } from "../../roomGovernance/roomGovernanceTypes";

/**
 * @typedef {import('../match/matchTypes').ManagerLease} ManagerLease
 */

/**
 * Map V1 room session match `managedBy` to manager lease holder.
 * @param {string | null | undefined} managedBy
 * @param {number} [epoch]
 * @returns {ManagerLease}
 */
export function leaseFromManagedBy(managedBy, epoch = 1) {
  return {
    holderUid: managedBy || null,
    epoch,
    expiresAtMs: null,
    heartbeatAtMs: null,
  };
}

/**
 * @param {ManagerLease | null | undefined} lease
 * @returns {string | null} V1 managedBy field
 */
export function managedByFromLease(lease) {
  return lease?.holderUid || null;
}

/**
 * @param {ManagerLease} lease
 * @param {string} newHolderUid
 * @param {number} nowMs
 * @param {number} ttlMs
 */
export function grantLease(lease, newHolderUid, nowMs, ttlMs) {
  const nextEpoch = (lease?.epoch || 0) + 1;
  return {
    holderUid: newHolderUid,
    epoch: nextEpoch,
    expiresAtMs: nowMs + ttlMs,
    heartbeatAtMs: nowMs,
  };
}

/**
 * @param {ManagerLease} lease
 * @param {string} uid
 * @param {number} nowMs
 */
export function heartbeatLease(lease, uid, nowMs) {
  if (!lease || lease.holderUid !== uid) return lease;
  return { ...lease, heartbeatAtMs: nowMs };
}

/**
 * @param {ManagerLease} lease
 * @param {number} [nowMs]
 */
export function isLeaseExpired(lease, nowMs = Date.now()) {
  if (!lease?.holderUid) return true;
  if (lease.expiresAtMs == null) return false;
  return nowMs >= lease.expiresAtMs;
}

/**
 * Align with room match manage lock staleness (V1 compatibility).
 * @param {{ lockedBy?: string | null, lockedAt?: { toMillis?: () => number } | number | null }} roomMatch
 * @param {number} [nowMs]
 */
export function isManageLockStaleForLease(roomMatch, nowMs = Date.now()) {
  if (!roomMatch?.lockedBy) return true;
  const raw = roomMatch.lockedAt;
  let lockedMs = 0;
  if (typeof raw === "number") lockedMs = raw;
  else if (raw && typeof raw.toMillis === "function") lockedMs = raw.toMillis();
  if (!lockedMs) return true;
  return nowMs - lockedMs >= ROOM_MATCH_LOCK_STALE_MS;
}

/**
 * @param {ManagerLease} lease
 * @param {string | null | undefined} uid
 * @param {number} baseVersion
 * @param {number} commandEpoch
 */
export function validateLeaseForCommand(lease, uid, baseVersion, commandEpoch) {
  if (!uid) return { ok: false, code: "UNAUTHENTICATED" };
  if (!lease?.holderUid || lease.holderUid !== uid) {
    return { ok: false, code: "LEASE_NOT_HOLDER" };
  }
  if (commandEpoch != null && commandEpoch !== lease.epoch) {
    return { ok: false, code: "LEASE_EPOCH_MISMATCH" };
  }
  if (baseVersion != null) {
    return { ok: true, baseVersion };
  }
  return { ok: true };
}

export { ROOM_MATCH_LOCK_STALE_MS as DEFAULT_LEASE_STALE_MS };
